import { useEffect, useRef } from "react";

// Stripe-style hero: vibrant animated gradient beams (cyan → blurple → pink → orange) on white,
// weighted to the right, with cursor parallax.
export default function HeroCanvas() {
  const ref = useRef(null);
  const mouse = useRef({ x: 0.7, y: 0.4 });

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let raf;
    let w, h;

    const palette = [
      [0, 212, 255],    // cyan
      [99, 91, 255],    // blurple
      [255, 89, 150],   // pink
      [255, 153, 102],  // orange
      [168, 140, 255],  // violet
      [0, 184, 212],    // teal
    ];

    const blobs = palette.map((c, i) => ({
      color: c,
      baseX: 0.38 + 0.68 * ((i * 0.41 + 0.09) % 1),
      baseY: -0.08 + 0.9 * ((i * 0.53 + 0.17) % 1),
      r: 0.30 + 0.14 * (i % 3),
      speed: 0.00015 + 0.00007 * i,
      phase: i * 1.7,
      amp: 0.13 + 0.05 * (i % 2),
    }));

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 1.6);
      w = rect.width; h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const onMouse = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.current = {
        x: (e.clientX - rect.left) / rect.width,
        y: (e.clientY - rect.top) / rect.height,
      };
    };
    window.addEventListener("mousemove", onMouse);

    const draw = (t) => {
      ctx.clearRect(0, 0, w, h);
      for (const b of blobs) {
        const px =
          (b.baseX + Math.sin(t * b.speed + b.phase) * b.amp +
            (mouse.current.x - 0.7) * 0.1) * w;
        const py =
          (b.baseY + Math.cos(t * b.speed * 1.25 + b.phase) * b.amp * 0.8 +
            (mouse.current.y - 0.4) * 0.08) * h;
        const radius = b.r * Math.max(w, h);
        const g = ctx.createRadialGradient(px, py, 0, px, py, radius);
        const [r, gg, bl] = b.color;
        g.addColorStop(0, `rgba(${r},${gg},${bl},0.55)`);
        g.addColorStop(0.5, `rgba(${r},${gg},${bl},0.28)`);
        g.addColorStop(1, "rgba(255,255,255,0)");
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(px, py, radius, 0, Math.PI * 2);
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMouse);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      data-testid="hero-canvas"
      className="absolute inset-0 w-full h-full"
    />
  );
}
